import { computePosition, flip, offset, shift } from "@floating-ui/dom";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { Placeholder, UndoRedo } from "@tiptap/extensions";
import { Editor, EditorContent, ReactRenderer, useEditor } from "@tiptap/react";
import { observer } from "mobx-react-lite";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useIsOutputMessage } from "../Playground/Drawer/Outputs/outputs-widget-context";

import ContextMenu, { ContextMenuHandle } from "./ContextMenu";
import { Commands, getSuggestionItems } from "./ContextMenu/utils";
import MarkdownStyleDecoration, {
  type InputVariableDecoration,
} from "./Decorations/MarkdownStyleDecorations";
import {
  ContentNodeType,
  EditorConfig,
  EditorConfigProvider,
  isNodeEnabled,
  useEditorConfig,
} from "./editor-config-context";
import { ClipboardHandler } from "./Extensions/ClipboardHandler";
import { MarkdownFormatting } from "./Extensions/MarkdownFormatting";
import { useDetectedInputVariableSuggestions, useEditorMode, useExternalChanges } from "./hooks";
import "./markdown-styles.css";
import DatasetExamplesNode from "./Nodes/DatasetExamples/DatasetExamplesNode";
import { VariableMention } from "./Nodes/InputVariable/VariableMention";
import SnippetNode from "./Nodes/Snippet/SnippetNode";
import { TagMention, useTagMentionSuggestion } from "./Nodes/Tag";
import { ContentAreaSize, ContentAreaSizeProvider } from "./size-context";
import { escapeXml, regexToHTML, serializeToMarkdown, unescapeXml } from "./utils";

import { useAuth } from "@/context/auth-context";
import { cn } from "@/lib/utils";
import { InputStyles } from "@/utils/inputStyles";

const getContentAreaSizeClass = (size: string): string => {
  if (size === "xs") {
    return "content-area-xs";
  }

  if (size === "sm") {
    return "content-area-sm";
  }

  return "content-area-md";
};

export interface ContentAreaRef {
  editor: Editor | null;
  focus: () => void;
}

interface TiptapEditorProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onCreate?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  containerClassName?: string;
  markdownEnabled?: boolean;
  hideContextMenu?: boolean;
  showLineNumbers?: boolean;
  size?: ContentAreaSize;
  enableTagging?: boolean;
  allowEntityTypeTags?: boolean;
  allowUserTags?: boolean;
  /** Custom keyboard handler. Return true to prevent default TipTap handling. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
  inputVariableDecorations?: Record<string, InputVariableDecoration>;
  onInputVariableClick?: (variableKey: string, target: HTMLElement) => void;
  /**
   * When true, the editor (both wrapper and inner ProseMirror element) stretches
   * to fill the height of its parent. Used by the snippet (Notion-canvas) editor.
   */
  fillHeight?: boolean;
}

const TiptapEditor = React.forwardRef<ContentAreaRef, TiptapEditorProps>(
  (
    {
      onChange,
      onCreate,
      placeholder: customPlaceholder,
      className,
      readOnly,
      value,
      containerClassName = "",
      markdownEnabled = true,
      hideContextMenu = false,
      showLineNumbers = false,
      size = "md",
      enableTagging = false,
      allowEntityTypeTags = false,
      allowUserTags = true,
      onKeyDown,
      inputVariableDecorations = {},
      onInputVariableClick,
      fillHeight = false,
    },
    ref,
  ) => {
    const contentClassName = useMemo(() => {
      let inputVariableCursorClassName = "";

      if (readOnly) {
        inputVariableCursorClassName = "cursor-default [&_.input-variable]:!cursor-default";
      } else if (!onInputVariableClick) {
        inputVariableCursorClassName =
          "[&_.input-variable]:!cursor-text [&_.input-variable]:!select-text";
      }

      return cn(
        InputStyles.Base,
        InputStyles.Textarea,
        "relative z-10 w-full overflow-auto flex-col",
        inputVariableCursorClassName,
        getContentAreaSizeClass(size),
        fillHeight && "!h-full !min-h-full",
        className,
      );
    }, [readOnly, onInputVariableClick, className, size, fillHeight]);

    const inputVariableDecorationsRef = useRef(inputVariableDecorations);
    inputVariableDecorationsRef.current = inputVariableDecorations;

    const getInputVariableDecorations = useCallback(() => inputVariableDecorationsRef.current, []);

    const editorConfig = useEditorConfig();
    const auth = useAuth();
    const inputVariableSuggestions = useDetectedInputVariableSuggestions(
      editorConfig.inputVariables,
    );
    const tagMentionSuggestion = useTagMentionSuggestion({ allowEntityTypeTags, allowUserTags });
    const mode = useEditorMode();

    const getInputVariableElementFromTarget = useCallback((target: EventTarget | null) => {
      if (target instanceof HTMLElement) {
        return target.closest<HTMLElement>('[data-input-variable="true"]') ?? null;
      }

      return null;
    }, []);

    const handleInputVariablePointer = useCallback(
      (event: MouseEvent) => {
        if (event.altKey || !onInputVariableClick) {
          return false;
        }

        const variableElement = getInputVariableElementFromTarget(event.target);
        const variableKey = variableElement?.dataset.inputVariableKey;

        if (!variableElement || !variableKey) {
          return false;
        }

        event.preventDefault();
        event.stopPropagation();
        onInputVariableClick(variableKey, variableElement);
        return true;
      },
      [getInputVariableElementFromTarget, onInputVariableClick],
    );

    const initialContent = useMemo(
      () => (value ? regexToHTML(escapeXml(String(value)), editorConfig.disabledNodes) : ""),
      [value, editorConfig.disabledNodes],
    );

    const handleUpdate = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ({ editor }: { editor: any }) => {
        if (onChange) {
          const serialized = unescapeXml(serializeToMarkdown(editor));
          onChange({
            target: { value: serialized },
          } as React.ChangeEvent<HTMLTextAreaElement>);
        }
      },
      [onChange],
    );

    const handleCreate = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ({ editor }: { editor: any }) => {
        if (onCreate) {
          const serialized = unescapeXml(serializeToMarkdown(editor));
          onCreate({
            target: { value: serialized },
          } as React.ChangeEvent<HTMLTextAreaElement>);
        }
      },
      [onCreate],
    );

    const isOutput = useIsOutputMessage();

    const extensions = useMemo(() => {
      const baseExtensions = [
        ClipboardHandler.configure({ mode, userToken: auth?.userToken ?? null }),
        MarkdownStyleDecoration.configure({
          mode,
          isOutput,
          markdownEnabled,
          getInputVariableDecorations,
        }),
        Document,
        Paragraph,
        Text,
        Placeholder.configure({
          placeholder: ({ node }): string => {
            if (typeof customPlaceholder === "string") {
              return customPlaceholder;
            }
            if (node.type.name === "heading") {
              return "What's the title?";
            }
            return 'Type "/" for commands...';
          },
          showOnlyWhenEditable: true,
          includeChildren: true,
        }),
        Commands.configure({
          suggestion: {
            char: "/",
            items: ({ query }: { query: string }) =>
              getSuggestionItems(query, hideContextMenu, mode),
            render: () => {
              let component: ReactRenderer<ContextMenuHandle>;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              let popup: any;

              if (hideContextMenu) {
                return null;
              }

              return {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onStart: (props: any) => {
                  component = new ReactRenderer(ContextMenu, {
                    props,
                    editor: props.editor,
                  });

                  if (!props.clientRect) {
                    return;
                  }

                  const element = component.element as HTMLElement;
                  element.style.position = "absolute";
                  element.style.zIndex = "9999";
                  element.setAttribute("data-commands-menu", "true");

                  // Add direct escape listener as fallback
                  const handleEscape = (e: KeyboardEvent) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      e.stopPropagation();
                      if (element.parentNode) {
                        element.parentNode.removeChild(element);
                      }
                      component?.destroy();
                    }
                  };

                  // Add blur handler to close menu when editor loses focus
                  const handleBlur = (e: FocusEvent) => {
                    // Check if the new focus target is inside the menu
                    const relatedTarget = e.relatedTarget as Node | null;
                    if (relatedTarget && element.contains(relatedTarget)) {
                      return;
                    }
                    // Small delay to allow click events on menu items to fire first
                    setTimeout(() => {
                      if (element.parentNode) {
                        element.parentNode.removeChild(element);
                      }
                      component?.destroy();
                    }, 150);
                  };

                  element.addEventListener("keydown", handleEscape, true);
                  document.addEventListener("keydown", handleEscape, true);
                  props.editor.options.element.addEventListener("blur", handleBlur, true);

                  // Store handlers for cleanup
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (element as any).__escapeHandler = handleEscape;
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (element as any).__blurHandler = handleBlur;
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (element as any).__editorElement = props.editor.options.element;

                  document.body.appendChild(element);

                  computePosition({ getBoundingClientRect: props.clientRect }, element, {
                    placement: "bottom-start",
                    middleware: [offset(6), flip(), shift({ padding: 8 })],
                  }).then(({ x, y }) => {
                    element.style.left = `${x}px`;
                    element.style.top = `${y}px`;
                  });

                  popup = { element };
                },

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onUpdate(props: any) {
                  component.updateProps(props);

                  if (!props.clientRect || !popup?.element) {
                    return;
                  }

                  computePosition({ getBoundingClientRect: props.clientRect }, popup.element, {
                    placement: "bottom-start",
                    middleware: [offset(6), flip(), shift({ padding: 8 })],
                  }).then(({ x, y }) => {
                    popup.element.style.left = `${x}px`;
                    popup.element.style.top = `${y}px`;
                  });
                },

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onKeyDown(props: any) {
                  if (props.event.key === "Escape") {
                    props.event.preventDefault();
                    props.event.stopPropagation();

                    if (component) {
                      const element = component.element as HTMLElement;
                      if (element?.parentNode) {
                        element.parentNode.removeChild(element);
                      }
                      component.destroy();
                    }
                    popup = null;
                    return true;
                  }

                  return component.ref?.onKeyDown(props.event) || false;
                },

                onExit() {
                  if (component) {
                    const element = component.element as HTMLElement;

                    // Clean up escape handler
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const escapeHandler = (element as any).__escapeHandler;
                    if (escapeHandler) {
                      element.removeEventListener("keydown", escapeHandler, true);
                      document.removeEventListener("keydown", escapeHandler, true);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      delete (element as any).__escapeHandler;
                    }

                    // Clean up blur handler
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const blurHandler = (element as any).__blurHandler;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const editorElement = (element as any).__editorElement;
                    if (blurHandler && editorElement) {
                      editorElement.removeEventListener("blur", blurHandler, true);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      delete (element as any).__blurHandler;
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      delete (element as any).__editorElement;
                    }

                    // Remove from DOM if still attached
                    if (element?.parentNode) {
                      element.parentNode.removeChild(element);
                    }

                    component.destroy();
                  }
                  popup = null;
                },
              };
            },
          },
        }),
        UndoRedo,
      ];

      // Add Snippet node if enabled
      if (isNodeEnabled(editorConfig, ContentNodeType.Snippet)) {
        baseExtensions.push(SnippetNode);
      }

      // Add DatasetExamples node if enabled
      if (isNodeEnabled(editorConfig, ContentNodeType.DatasetExamples)) {
        baseExtensions.push(DatasetExamplesNode);
      }

      // Add InputVariable (VariableMention) node if enabled
      if (isNodeEnabled(editorConfig, ContentNodeType.InputVariable)) {
        baseExtensions.push(
          VariableMention.configure({
            mode,
            suggestion: {
              ...inputVariableSuggestions,
              char: "{",
            },
          }),
        );
      }

      if (markdownEnabled) {
        baseExtensions.push(MarkdownFormatting);
      }

      // Add TagMention extension when tagging is enabled and Tag node is not disabled
      if (enableTagging && isNodeEnabled(editorConfig, ContentNodeType.Tag)) {
        baseExtensions.push(
          TagMention.configure({
            suggestion: tagMentionSuggestion,
          }),
        );
      }

      return baseExtensions;
    }, [
      auth?.userToken,
      markdownEnabled,
      hideContextMenu,
      mode,
      isOutput,
      inputVariableSuggestions,
      customPlaceholder,
      enableTagging,
      tagMentionSuggestion,
      editorConfig,
      getInputVariableDecorations,
    ]);

    const editor = useEditor({
      extensions,
      parseOptions: {
        preserveWhitespace: "full",
      },
      editorProps: {
        editable: () => !readOnly,
        attributes: {
          class: `${contentClassName}`,
        },
        handleDOMEvents: {
          mousedown: (_view, event) => handleInputVariablePointer(event),
        },
        handleClick: (_view, _pos, event) => handleInputVariablePointer(event),
        handleKeyDown: (view, event) => {
          if (onKeyDown?.(event as unknown as React.KeyboardEvent<HTMLTextAreaElement>)) {
            return true;
          }
          if (event.key === "Tab") {
            event.preventDefault();
            view.dispatch(view.state.tr.insertText("\t"));
            return true;
          }
          return false;
        },
      },
      content: initialContent,
      onCreate: handleCreate,
      onUpdate: handleUpdate,
    });

    useExternalChanges(editor, initialContent);

    useEffect(() => {
      if (!editor) {
        return;
      }

      editor.view.dispatch(editor.state.tr.setMeta("inputVariableDecorations", true));
    }, [editor, inputVariableDecorations]);

    // Ensure editor editable state is updated when readOnly changes
    useEffect(() => {
      if (editor) {
        editor.setEditable(!readOnly);
      }
    }, [editor, readOnly]);

    // Size the line-number gutter to the widest line number so large counts
    // (tens of thousands, millions) don't overflow or clip the fixed gutter.
    const [lineNumberDigits, setLineNumberDigits] = useState(2);
    useEffect(() => {
      if (!editor || !showLineNumbers) {
        return;
      }

      const updateDigits = () => {
        const lineCount = editor.state.doc.childCount;
        setLineNumberDigits(Math.max(2, String(lineCount).length));
      };

      updateDigits();
      editor.on("update", updateDigits);
      return () => {
        editor.off("update", updateDigits);
      };
    }, [editor, showLineNumbers]);

    useImperativeHandle(
      ref,
      () => ({
        editor,
        focus: () => {
          editor?.commands.focus();
        },
      }),
      [editor],
    );

    return (
      <ContentAreaSizeProvider size={size}>
        <EditorContent
          editor={editor}
          style={
            showLineNumbers
              ? ({ "--ln-digits": lineNumberDigits } as React.CSSProperties)
              : undefined
          }
          className={cn(
            "content-editor outline-none",
            showLineNumbers && "content-editor--line-numbers",
            fillHeight && "h-full [&>.ProseMirror]:h-full [&>.ProseMirror]:min-h-full",
            containerClassName,
          )}
        />
      </ContentAreaSizeProvider>
    );
  },
);

TiptapEditor.displayName = "TiptapEditor";

interface ContentAreaProps extends TiptapEditorProps {
  markdownEnabled?: boolean;
  hideContextMenu?: boolean;
  size?: ContentAreaSize;
  config?: EditorConfig;
  enableTagging?: boolean;
  allowEntityTypeTags?: boolean;
  allowUserTags?: boolean;
  fillHeight?: boolean;
}

const ContentArea = observer(
  React.forwardRef<ContentAreaRef, ContentAreaProps>(
    (
      {
        markdownEnabled = true,
        size = "md",
        config,
        enableTagging = false,
        allowEntityTypeTags = false,
        allowUserTags = true,
        ...props
      },
      ref,
    ) => {
      const mode = useEditorMode();

      return (
        <EditorConfigProvider config={config}>
          <TiptapEditor
            ref={ref}
            {...props}
            size={size}
            markdownEnabled={markdownEnabled}
            enableTagging={enableTagging}
            allowEntityTypeTags={allowEntityTypeTags}
            allowUserTags={allowUserTags}
            key={`${mode}-${markdownEnabled}-${enableTagging}-${config?.disabledNodes?.join(",") ?? ""}-${allowEntityTypeTags}-${allowUserTags}`}
          />
        </EditorConfigProvider>
      );
    },
  ),
);

ContentArea.displayName = "ContentArea";

export { ContentNodeType } from "./editor-config-context";
export default ContentArea;
